import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Crown, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function RegisterPage() {
    useSeo({
        title: "Forge Your Sigil",
        description:
            "Create your EIGOO Books account to track pages read, chapters completed, and reading streaks across every animated comic in the library.",
    });
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await register(form.email, form.password, form.name);
            navigate("/", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="register-page" className="min-h-[80vh] flex items-center justify-center px-6 py-20">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <Crown className="w-7 h-7 text-[#D4AF37] mx-auto mb-4" />
                    <div className="eyebrow mb-2">Begin Reading</div>
                    <h1 className="font-serif text-4xl text-white">Forge your sigil</h1>
                    <p className="text-[#A1A1AA] mt-3 text-sm">
                        Track every page, every chapter, every streak.
                    </p>
                </div>

                <form
                    onSubmit={submit}
                    className="frame-gold p-8 space-y-5"
                    data-testid="register-form"
                >
                    <div className="space-y-1.5">
                        <label className="eyebrow text-[10px] block">Name</label>
                        <input
                            data-testid="register-name"
                            type="text"
                            required
                            minLength={1}
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="eyebrow text-[10px] block">Email</label>
                        <input
                            data-testid="register-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="eyebrow text-[10px] block">Password (min 6)</label>
                        <input
                            data-testid="register-password"
                            type="password"
                            required
                            minLength={6}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </div>

                    {error && (
                        <div
                            data-testid="register-error"
                            className="border border-[#8A0303]/50 bg-[#8A0303]/10 px-4 py-3 text-sm text-[#cd6a6a]"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        data-testid="register-submit"
                        disabled={loading}
                        className="btn-gold w-full justify-center disabled:opacity-50"
                    >
                        {loading ? "Forging…" : "Forge sigil"}
                        <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </form>

                <p className="text-center text-sm text-[#A1A1AA] mt-6">
                    Already inscribed?{" "}
                    <Link
                        to="/login"
                        data-testid="link-login"
                        className="text-[#D4AF37] hover:text-[#E6D59A]"
                    >
                        Enter the chamber →
                    </Link>
                </p>
            </div>
        </main>
    );
}
